import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  ShieldCheck,
  Sparkles,
  TriangleAlert,
} from "lucide-react";
import { api, formatApiError, getOutOfAreaPayload } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PLANS = {
  monthly: {
    code: "monthly",
    name: "Monthly",
    blurb: "Up to 2 bins · 1 cleaning per month.",
    perBin: false,
    cents: 2500,
    suffix: "/ mo",
  },
  biweekly: {
    code: "biweekly",
    name: "Bi-Weekly",
    blurb: "Up to 2 bins · 2 cleanings per month.",
    perBin: false,
    cents: 4000,
    suffix: "/ mo",
    most_popular: true,
  },
  onetime: {
    code: "onetime",
    name: "One-Time",
    blurb: "Per bin · single deep clean.",
    perBin: true,
    cents: 4000,
    suffix: "/ bin",
  },
};

const SERVICE_AREA_ZIPS = new Set([
  "08021", "08004", "08081", "08037", "08009", "08012",
  "08080", "08028", "08096", "08094",
]);

const SERVICE_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const STEPS = [
  { id: 1, label: "Plan" },
  { id: 2, label: "Account" },
  { id: 3, label: "Address" },
  { id: 4, label: "Service Day" },
  { id: 5, label: "Review & Pay" },
];

const EXTRA_BIN_PRICE_CENTS = 800;

const computePrice = (planCode, bins) => {
  const n = Math.max(1, bins);
  if (planCode === "monthly") {
    return 2500 + Math.max(0, n - 2) * EXTRA_BIN_PRICE_CENTS;
  }
  if (planCode === "biweekly") {
    // $40 base for 2 bins + $8 per extra bin (mirrors backend
    // calculate_plan_price_cents — single source of truth for Step 5 total)
    return 4000 + Math.max(0, n - 2) * EXTRA_BIN_PRICE_CENTS;
  }
  if (planCode === "onetime") {
    return 4000 * n;
  }
  return 0;
};

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setSession, isAuthed } = useAuth();

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [mobileSummaryOpen, setMobileSummaryOpen] = useState(false);

  const initialPlan = PLANS[searchParams.get("plan")] ? searchParams.get("plan") : "monthly";
  const initialBins = Math.max(
    1,
    Math.min(10, parseInt(searchParams.get("bins") || "2", 10) || 2),
  );
  const initialZip = (searchParams.get("zip") || "").replace(/\D/g, "").slice(0, 5);
  const initialPromo = (searchParams.get("promo") || "").trim().toUpperCase();
  const initialStepRaw = parseInt(searchParams.get("step") || "1", 10);
  const initialStep = Number.isFinite(initialStepRaw) && initialStepRaw >= 1 && initialStepRaw <= 5
    ? initialStepRaw
    : 1;
  const wasCanceled = searchParams.get("canceled") === "1";
  const hadError = searchParams.get("error") === "payment_failed";

  const [form, setForm] = useState(() => ({
    plan_code: initialPlan,
    bin_count: initialBins,
    full_name: "",
    email: "",
    password: "",
    password_confirm: "",
    phone: "",
    address_line1: "",
    city: "",
    state: "NJ",
    zip: initialZip,
    service_day: "Wed",
    promo_code: initialPromo,
  }));
  const [errors, setErrors] = useState({});
  // promoState: { status: 'idle'|'checking'|'valid'|'invalid', message, discount_percent }
  const [promoState, setPromoState] = useState({
    status: initialPromo ? "pending" : "idle",
    message: "",
    discount_percent: 0,
  });

  // Override initial step from ?step= URL param (used when Stripe redirects
  // back to the cancel_url, or when SignupSuccess bounces on 402).
  useEffect(() => {
    if (initialStep > 1) setStep(initialStep);
    if (wasCanceled) {
      toast.info("No charge — you canceled at checkout", {
        description: "Your details are still here. Review and try again when ready.",
      });
    }
    if (hadError) {
      toast.error("Payment didn't complete", {
        description: "The card may have been declined. Try again or use a different card.",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Defensive reset — if the component is restored from bfcache (user hits
  // Back from Stripe Checkout), React state can survive as-is. Force
  // `submitting` back to false so the CTA re-enables and doesn't look
  // permanently "Redirecting…".
  useEffect(() => {
    const onPageShow = (ev) => {
      if (ev.persisted) setSubmitting(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  useEffect(() => {
    if (isAuthed) navigate("/portal", { replace: true });
  }, [isAuthed, navigate]);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validateStep = () => {
    const e = {};
    if (step === 1) {
      if (!PLANS[form.plan_code]) e.plan_code = "Pick a plan.";
      if (form.bin_count < 1) e.bin_count = "At least 1 bin.";
    }
    if (step === 2) {
      if (form.full_name.trim().length < 2) e.full_name = "Tell us your name.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
        e.email = "Enter a valid email.";
      if (form.password.length < 8)
        e.password = "Password must be 8+ characters.";
      if (form.password !== form.password_confirm)
        e.password_confirm = "Passwords don't match.";
    }
    if (step === 3) {
      if (form.address_line1.trim().length < 2)
        e.address_line1 = "Street address required.";
      if (form.city.trim().length < 2) e.city = "City required.";
      if (!/^\d{5}$/.test(form.zip)) e.zip = "5-digit zip.";
    }
    if (step === 4) {
      if (!SERVICE_DAYS.includes(form.service_day))
        e.service_day = "Pick a day.";
    }
    // Step 5 has no required client-side fields — payment is captured on Stripe Checkout.
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep((s) => Math.min(STEPS.length, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const validatePromo = async () => {
    const code = (form.promo_code || "").trim().toUpperCase();
    if (!code) {
      setPromoState({ status: "idle", message: "", discount_percent: 0 });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      // Need a valid email to validate against new-customer rules. Mark pending.
      setPromoState({
        status: "pending",
        message: "Enter your email above first to apply.",
        discount_percent: 0,
      });
      return;
    }
    setPromoState((s) => ({ ...s, status: "checking" }));
    try {
      const { data } = await api.post("/coupons/validate", {
        code,
        plan_code: form.plan_code,
        email: form.email.trim().toLowerCase(),
      });
      if (data.valid) {
        setPromoState({
          status: "valid",
          message: data.message,
          discount_percent: data.discount_percent || 0,
        });
        setForm((f) => ({ ...f, promo_code: code }));
      } else {
        setPromoState({
          status: "invalid",
          message: data.message,
          discount_percent: 0,
        });
      }
    } catch (err) {
      setPromoState({
        status: "invalid",
        message: formatApiError(err),
        discount_percent: 0,
      });
    }
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        phone: form.phone.trim() || null,
        address_line1: form.address_line1.trim(),
        city: form.city.trim(),
        state: form.state.trim().toUpperCase(),
        zip: form.zip,
        service_day: form.service_day,
        plan_code: form.plan_code,
        bin_count: form.bin_count,
        origin_url: window.location.origin,
      };
      const cleanPromo = (form.promo_code || "").trim().toUpperCase();
      if (cleanPromo && promoState.status !== "invalid") {
        payload.promo_code = cleanPromo;
      }
      const { data } = await api.post("/auth/signup", payload);
      if (data?.coupon_warning) {
        toast.warning("Promo code skipped", {
          description: data.coupon_warning.message,
        });
      }
      if (!data?.checkout_url) {
        throw new Error("Stripe checkout URL missing from server response");
      }
      // Hand off to Stripe — full page redirect, no token yet.
      window.location.href = data.checkout_url;
    } catch (err) {
      const oa = getOutOfAreaPayload(err);
      if (oa) {
        toast.error("Out of service area", {
          description: `${oa.zip} isn't on our routes yet.`,
        });
        setStep(3);
      } else if (err?.response?.status === 409) {
        toast.error("Email already registered", {
          description: "Sign in with that email instead.",
        });
        setStep(2);
      } else {
        toast.error("Couldn't start checkout", {
          description: formatApiError(err),
        });
      }
      setSubmitting(false);
    }
  };

  const priceCents = computePrice(form.plan_code, form.bin_count);
  const priceDollars = (priceCents / 100).toFixed(0);
  const monthlyExtraBins =
    form.plan_code === "monthly" ? Math.max(0, form.bin_count - 2) : 0;
  const zipOutOfArea = useMemo(
    () => /^\d{5}$/.test(form.zip) && !SERVICE_AREA_ZIPS.has(form.zip),
    [form.zip],
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-50 to-white" data-testid="page-signup">
      <section className="container-page pt-6 md:pt-10 lg:pt-16 pb-3 lg:pb-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-500 hover:text-brand-700 transition-colors mb-3 lg:mb-5"
          data-testid="signup-back-home"
        >
          <ArrowLeft size={12} /> Back home
        </Link>
        <p className="overline mb-1.5 lg:mb-3">Get Started</p>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] text-ink text-balance break-words">
          Fresh bins in <span className="text-brand">5 quick steps.</span>
        </h1>
        <p className="mt-2 lg:mt-4 text-[13px] lg:text-base text-slate-600 max-w-2xl">
          No contracts. Cancel anytime.
        </p>
      </section>

      <section className="container-page pb-32 lg:pb-24">
        <div className="grid lg:grid-cols-12 gap-4 lg:gap-8">
          {/* Form column */}
          <div className="lg:col-span-8">
            {/* Stepper — compact on mobile (numbers only + active label), full on desktop */}
            <ol
              className="flex items-center gap-x-1.5 lg:gap-x-2 gap-y-3 mb-4 lg:mb-7"
              data-testid="signup-stepper"
            >
              {STEPS.map((s, i) => {
                const active = step === s.id;
                const done = step > s.id;
                return (
                  <li key={s.id} className="flex items-center gap-1.5 lg:gap-2">
                    <span
                      className={`inline-flex h-7 w-7 lg:h-8 lg:w-8 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                        active
                          ? "bg-brand text-white shadow-soft"
                          : done
                            ? "bg-accent text-white"
                            : "bg-white border border-slate-200 text-slate-500"
                      }`}
                      data-testid={`signup-step-${s.id}`}
                    >
                      {done ? <Check size={12} /> : s.id}
                    </span>
                    <span
                      className={`text-[10px] lg:text-[11px] uppercase tracking-[0.18em] lg:tracking-[0.2em] ${
                        active ? "text-ink font-bold inline" : "text-slate-500 hidden lg:inline"
                      }`}
                    >
                      {s.label}
                    </span>
                    {i < STEPS.length - 1 && (
                      <span className="hidden sm:inline-block h-px w-3 lg:w-6 bg-slate-200 mx-0.5 lg:mx-1" />
                    )}
                  </li>
                );
              })}
            </ol>

            <p className="text-[11px] lg:text-xs text-slate-500 mb-3 lg:mb-4" data-testid="signup-step-counter">
              Step {step} of {STEPS.length}
            </p>

            <form
              onSubmit={(e) => e.preventDefault()}
              noValidate
              className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 lg:p-9 shadow-soft"
              data-testid="signup-form"
            >
              {step === 1 && (
                <div data-testid="signup-step-1-content">
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-ink mb-1">
                    Pick your plan
                  </h2>
                  <p className="text-sm text-slate-500 mb-6">
                    You can switch any time from your portal.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-7">
                    {Object.values(PLANS).map((p) => {
                      const selected = form.plan_code === p.code;
                      return (
                        <button
                          key={p.code}
                          type="button"
                          onClick={() => update("plan_code", p.code)}
                          className={`relative rounded-2xl border-2 p-5 text-left transition-all ${
                            selected
                              ? "border-brand bg-brand-50 shadow-lift -translate-y-0.5"
                              : "border-slate-200 bg-white hover:border-brand/40"
                          } ${p.most_popular && !selected ? "border-l-[6px] border-l-gold" : ""}`}
                          data-testid={`signup-plan-${p.code}`}
                        >
                          {p.most_popular && (
                            <span className="absolute -top-2.5 right-3 inline-flex items-center gap-1 bg-accent text-white text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full shadow-soft">
                              <Sparkles size={10} /> Popular
                            </span>
                          )}
                          <div className="font-heading text-xl font-bold text-ink mb-1">
                            {p.name}
                          </div>
                          <div className="text-xs text-slate-500 mb-3 leading-relaxed">
                            {p.blurb}
                          </div>
                          <div className="font-heading text-3xl font-extrabold text-brand">
                            ${(p.cents / 100).toFixed(0)}
                            <span className="text-xs text-slate-500 font-sans font-normal ml-1">
                              {p.suffix}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <Field
                    label="How many bins?"
                    id="bin_count"
                    testId="signup-bins-wrap"
                  >
                    <select
                      id="bin_count"
                      value={form.bin_count}
                      onChange={(e) =>
                        update("bin_count", parseInt(e.target.value, 10))
                      }
                      className="signup-input"
                      data-testid="signup-input-bins"
                    >
                      {[1, 2, 3, 4, 5, 6].map((n) => (
                        <option key={n} value={n}>
                          {n} {n === 1 ? "bin" : "bins"}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <div
                    className="mt-4 text-sm text-slate-700"
                    data-testid="signup-price-estimate"
                  >
                    Est.{" "}
                    <span className="font-bold text-ink">${priceDollars}</span>{" "}
                    <span className="text-slate-500">
                      {form.plan_code === "onetime" ? "one-time" : "/ mo"}
                    </span>{" "}
                    for {form.bin_count}{" "}
                    {form.bin_count === 1 ? "bin" : "bins"} on the{" "}
                    {PLANS[form.plan_code].name} plan.
                    {monthlyExtraBins > 0 && (
                      <span
                        className="block mt-1 text-xs text-slate-500"
                        data-testid="signup-monthly-breakdown"
                      >
                        $25 base + $8 × {monthlyExtraBins} extra bin
                        {monthlyExtraBins === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div data-testid="signup-step-2-content">
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-ink mb-1">
                    Create your account
                  </h2>
                  <p className="text-sm text-slate-500 mb-6">
                    You'll use this to sign in to your portal.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Full name" id="full_name" error={errors.full_name} testId="signup-fn">
                      <input
                        id="full_name"
                        value={form.full_name}
                        onChange={(e) => update("full_name", e.target.value)}
                        className="signup-input"
                        placeholder="Jane Smith"
                        data-testid="signup-input-name"
                      />
                    </Field>
                    <Field label="Email" id="email" error={errors.email} testId="signup-em">
                      <input
                        id="email"
                        type="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        className="signup-input"
                        placeholder="you@email.com"
                        data-testid="signup-input-email"
                      />
                    </Field>
                    <Field
                      label="Password"
                      id="password"
                      error={errors.password}
                      testId="signup-pw"
                    >
                      <div className="relative">
                        <input
                          id="password"
                          type={showPw ? "text" : "password"}
                          autoComplete="new-password"
                          value={form.password}
                          onChange={(e) => update("password", e.target.value)}
                          className="signup-input pr-12"
                          placeholder="8+ characters"
                          data-testid="signup-input-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPw((s) => !s)}
                          aria-label={showPw ? "Hide password" : "Show password"}
                          className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 inline-flex items-center justify-center rounded-lg text-slate-500 hover:text-brand-700 hover:bg-slate-100"
                          data-testid="signup-toggle-password"
                        >
                          {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </Field>
                    <Field
                      label="Confirm password"
                      id="password_confirm"
                      error={errors.password_confirm}
                      testId="signup-pwc"
                    >
                      <input
                        id="password_confirm"
                        type={showPw ? "text" : "password"}
                        autoComplete="new-password"
                        value={form.password_confirm}
                        onChange={(e) =>
                          update("password_confirm", e.target.value)
                        }
                        className="signup-input"
                        data-testid="signup-input-password-confirm"
                      />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Phone (optional)" id="phone" testId="signup-ph">
                        <input
                          id="phone"
                          type="tel"
                          value={form.phone}
                          onChange={(e) => update("phone", e.target.value)}
                          className="signup-input"
                          placeholder="(856) 555-0000"
                          data-testid="signup-input-phone"
                        />
                      </Field>
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div data-testid="signup-step-3-content">
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-ink mb-1">
                    Service address
                  </h2>
                  <p className="text-sm text-slate-500 mb-6">
                    We currently serve Pine Hill and nearby South Jersey
                    communities.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-6 gap-5">
                    <div className="sm:col-span-6">
                      <Field
                        label="Street address"
                        id="address"
                        error={errors.address_line1}
                        testId="signup-addr"
                      >
                        <input
                          id="address"
                          value={form.address_line1}
                          onChange={(e) =>
                            update("address_line1", e.target.value)
                          }
                          className="signup-input"
                          placeholder="123 Maple St"
                          data-testid="signup-input-address"
                        />
                      </Field>
                    </div>
                    <div className="sm:col-span-3">
                      <Field label="City" id="city" error={errors.city} testId="signup-city">
                        <input
                          id="city"
                          value={form.city}
                          onChange={(e) => update("city", e.target.value)}
                          className="signup-input"
                          placeholder="Pine Hill"
                          data-testid="signup-input-city"
                        />
                      </Field>
                    </div>
                    <div className="sm:col-span-1">
                      <Field label="State" id="state" testId="signup-state">
                        <input
                          id="state"
                          value={form.state}
                          onChange={(e) =>
                            update("state", e.target.value.toUpperCase())
                          }
                          className="signup-input"
                          maxLength={2}
                          data-testid="signup-input-state"
                        />
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <Field label="Zip" id="zip" error={errors.zip} testId="signup-zip">
                        <input
                          id="zip"
                          inputMode="numeric"
                          value={form.zip}
                          onChange={(e) =>
                            update(
                              "zip",
                              e.target.value.replace(/\D/g, "").slice(0, 5),
                            )
                          }
                          className="signup-input"
                          placeholder="08021"
                          data-testid="signup-input-zip"
                        />
                      </Field>
                    </div>
                  </div>

                  {zipOutOfArea && (
                    <div
                      className="mt-5 rounded-xl bg-amber-50 border border-amber-200 p-5 text-sm text-amber-900"
                      data-testid="signup-zip-out-of-area"
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <TriangleAlert size={16} className="mt-0.5 shrink-0 text-amber-600" />
                        <p className="font-semibold">
                          We don't have regular routes to {form.zip} yet.
                        </p>
                      </div>
                      <p className="ml-6 leading-relaxed">
                        Submit a{" "}
                        <Link
                          to="/#quote"
                          className="font-bold text-brand-700 hover:underline"
                          data-testid="signup-custom-quote-link"
                        >
                          custom quote →
                        </Link>{" "}
                        and we'll see what we can do.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {step === 4 && (
                <div data-testid="signup-step-4-content">
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-ink mb-1">
                    Pick your service day
                  </h2>
                  <p className="text-sm text-slate-500 mb-6">
                    The day of the week we'll show up. Usually the day after
                    your trash pickup.
                  </p>
                  <div
                    className="grid grid-cols-3 sm:grid-cols-6 gap-3"
                    role="radiogroup"
                    aria-label="Service day"
                  >
                    {SERVICE_DAYS.map((d) => {
                      const selected = form.service_day === d;
                      return (
                        <button
                          key={d}
                          type="button"
                          onClick={() => update("service_day", d)}
                          aria-pressed={selected}
                          className={`rounded-xl border-2 py-4 text-sm font-bold transition-all ${
                            selected
                              ? "border-brand bg-brand text-white -translate-y-0.5 shadow-soft"
                              : "border-slate-200 bg-white text-ink hover:border-brand/40"
                          }`}
                          data-testid={`signup-day-${d.toLowerCase()}`}
                        >
                          {d}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {step === 5 && (
                <div data-testid="signup-step-5-content">
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-ink mb-1">
                    Review & pay
                  </h2>
                  <p className="text-xs text-slate-500 mb-5 inline-flex items-center gap-2">
                    <ShieldCheck size={14} className="text-accent" />
                    You'll be redirected to Stripe Checkout to complete payment.
                  </p>

                  <div
                    className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 mb-5"
                    data-testid="signup-review-card"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">Plan</p>
                      <p className="font-heading text-lg font-extrabold text-ink" data-testid="signup-review-plan">
                        {PLANS[form.plan_code]?.name} · {form.bin_count} bin
                        {form.bin_count === 1 ? "" : "s"}
                      </p>
                    </div>
                    <ul className="space-y-2 text-sm text-slate-700">
                      <li className="flex items-center justify-between">
                        <span className="text-slate-500">Service day</span>
                        <span className="font-semibold text-ink">{form.service_day}</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span className="text-slate-500">Address</span>
                        <span className="font-semibold text-ink truncate max-w-[60%] text-right">
                          {form.address_line1}, {form.zip}
                        </span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span className="text-slate-500">Email</span>
                        <span className="font-semibold text-ink truncate max-w-[60%] text-right">
                          {form.email || "—"}
                        </span>
                      </li>
                    </ul>
                    <div className="mt-4 pt-4 border-t border-slate-100 flex items-baseline justify-between">
                      <span className="text-[11px] uppercase tracking-[0.2em] text-brand-700 font-bold">
                        {form.plan_code === "onetime" ? "One-time total" : "Today's charge"}
                      </span>
                      <span className="font-heading text-2xl font-extrabold text-ink" data-testid="signup-review-total">
                        $
                        {promoState.status === "valid" && promoState.discount_percent > 0
                          ? (priceCents * (100 - promoState.discount_percent) / 10000).toFixed(2)
                          : priceDollars}
                        <span className="text-xs text-slate-500 font-sans font-normal ml-1">
                          {form.plan_code === "onetime" ? "" : "/ mo"}
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Promo code */}
                  <div className="pt-5 border-t border-slate-100">
                    <label
                      htmlFor="signup-promo"
                      className="block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-2"
                    >
                      Promo code <span className="text-slate-400 font-normal normal-case tracking-normal text-xs">(optional)</span>
                    </label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        id="signup-promo"
                        value={form.promo_code}
                        onChange={(e) => {
                          update("promo_code", e.target.value.toUpperCase().slice(0, 40));
                          if (promoState.status === "valid" || promoState.status === "invalid") {
                            setPromoState({ status: "idle", message: "", discount_percent: 0 });
                          }
                        }}
                        onBlur={validatePromo}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            validatePromo();
                          }
                        }}
                        placeholder="e.g. CANS25"
                        className="signup-input uppercase tracking-[0.18em] font-bold flex-1"
                        data-testid="signup-input-promo"
                      />
                      <button
                        type="button"
                        onClick={validatePromo}
                        disabled={
                          !form.promo_code ||
                          promoState.status === "checking" ||
                          promoState.status === "valid"
                        }
                        className="btn-secondary sm:w-auto whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="signup-promo-apply"
                      >
                        {promoState.status === "checking" ? "Checking…" : promoState.status === "valid" ? "Applied" : "Apply"}
                      </button>
                    </div>
                    {promoState.status === "valid" && (
                      <p
                        className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 border border-accent-200 text-xs font-semibold text-accent-700"
                        data-testid="signup-promo-valid"
                      >
                        <Check size={12} />
                        {form.promo_code} applied. {promoState.discount_percent}% off your first payment.
                      </p>
                    )}
                    {promoState.status === "invalid" && (
                      <p
                        className="mt-3 inline-flex items-start gap-2 text-xs font-semibold text-red-600"
                        data-testid="signup-promo-invalid"
                      >
                        <TriangleAlert size={12} className="mt-0.5 shrink-0" />
                        {promoState.message}
                      </p>
                    )}
                    {promoState.status === "pending" && (
                      <p className="mt-3 text-xs text-slate-500">{promoState.message}</p>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-6 lg:mt-9 sticky bottom-0 lg:static -mx-5 sm:-mx-6 lg:mx-0 -mb-5 sm:-mb-6 lg:mb-0 px-5 sm:px-6 lg:px-0 pb-[calc(0.75rem+env(safe-area-inset-bottom))] lg:pb-0 pt-3 lg:pt-6 border-t border-slate-100 bg-white/95 lg:bg-transparent backdrop-blur lg:backdrop-blur-0 z-10">
                {/* Mobile/tablet summary bar — collapsible */}
                <div className="lg:hidden mb-2.5" data-testid="signup-mobile-summary">
                  <button
                    type="button"
                    onClick={() => setMobileSummaryOpen((o) => !o)}
                    className="w-full flex items-center justify-between gap-3 rounded-xl bg-brand-50 border border-brand-100 px-3.5 py-2.5 text-left"
                    data-testid="signup-mobile-summary-toggle"
                    aria-expanded={mobileSummaryOpen}
                  >
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-brand-700 font-bold">
                        {form.plan_code === "onetime" ? "One-time total" : "Today's charge"}
                      </p>
                      <p
                        className="font-heading font-extrabold text-ink text-xl leading-tight"
                        data-testid="signup-mobile-summary-total"
                      >
                        $
                        {promoState.status === "valid" && promoState.discount_percent > 0
                          ? (priceCents * (100 - promoState.discount_percent) / 10000).toFixed(2)
                          : priceDollars}
                        <span className="text-xs text-slate-500 font-sans font-normal ml-1">
                          {form.plan_code === "onetime" ? "" : "/ mo"}
                        </span>
                        {promoState.status === "valid" && promoState.discount_percent > 0 && (
                          <span className="ml-2 text-xs text-slate-400 line-through font-sans font-normal">
                            ${priceDollars}
                          </span>
                        )}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-700 shrink-0">
                      {mobileSummaryOpen ? "Hide" : "Details"}
                      {mobileSummaryOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </span>
                  </button>
                  {mobileSummaryOpen && (
                    <ul
                      className="mt-2 space-y-1.5 text-[12px] text-slate-700 px-3.5 py-3 rounded-xl bg-white border border-slate-100"
                      data-testid="signup-mobile-summary-details"
                    >
                      <li className="flex items-center justify-between">
                        <span className="text-slate-500">Plan</span>
                        <span className="font-semibold text-ink">{PLANS[form.plan_code]?.name}</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span className="text-slate-500">Bins</span>
                        <span className="font-semibold text-ink">{form.bin_count}</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span className="text-slate-500">Service day</span>
                        <span className="font-semibold text-ink">{form.service_day}</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span className="text-slate-500">Address zip</span>
                        <span className="font-semibold text-ink">{form.zip || "—"}</span>
                      </li>
                      {monthlyExtraBins > 0 && (
                        <li className="text-[11px] text-slate-500 pt-1 border-t border-slate-100">
                          $25 base + $8 × {monthlyExtraBins} extra bin
                          {monthlyExtraBins === 1 ? "" : "s"}
                        </li>
                      )}
                      {promoState.status === "valid" && promoState.discount_percent > 0 && (
                        <li className="text-[11px] font-bold text-accent-700 pt-1 inline-flex items-center gap-1.5">
                          <Sparkles size={10} />
                          {form.promo_code} saves ${(priceCents * promoState.discount_percent / 10000).toFixed(2)}
                        </li>
                      )}
                    </ul>
                  )}
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2.5 lg:gap-3">
                  {step > 1 ? (
                    <button
                      type="button"
                      onClick={back}
                      className="btn-secondary w-full sm:w-auto h-11 lg:h-auto"
                      data-testid="signup-back"
                    >
                      <ArrowLeft size={14} /> Back
                    </button>
                  ) : (
                    <span className="hidden sm:block" />
                  )}
                  {step < STEPS.length ? (
                    <button
                      type="button"
                      onClick={next}
                      className="btn-primary w-full sm:w-auto h-11 lg:h-auto"
                      data-testid="signup-next"
                    >
                      Continue <ArrowRight size={14} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={submit}
                      disabled={submitting}
                      className="btn-primary w-full sm:w-auto h-11 lg:h-auto disabled:opacity-60"
                      data-testid="signup-submit"
                    >
                      {submitting ? "Redirecting to Stripe…" : "Continue to Secure Checkout"}
                      {!submitting && <ArrowRight size={14} />}
                    </button>
                  )}
                </div>
              </div>
            </form>

            <p className="mt-4 lg:mt-5 text-center text-[13px] lg:text-sm text-slate-600">
              Already a customer?{" "}
              <Link
                to="/signin"
                className="font-semibold text-brand-700 hover:text-brand"
                data-testid="signup-signin-link"
              >
                Sign in →
              </Link>
            </p>
          </div>

          {/* Order summary — desktop only (mobile uses sticky bottom summary) */}
          <aside className="hidden lg:block lg:col-span-4">
            <div className="lg:sticky lg:top-28 bg-white border border-slate-200 rounded-2xl p-7 shadow-soft">
              <p className="overline mb-3">Your service</p>
              <h3 className="font-heading text-2xl font-bold text-ink mb-5">
                {PLANS[form.plan_code]?.name} plan
              </h3>
              <ul className="space-y-3 text-sm text-slate-700 mb-6">
                <li className="flex items-center justify-between">
                  <span className="text-slate-500">Bins</span>
                  <span className="font-semibold text-ink">
                    {form.bin_count}
                  </span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-slate-500">Service day</span>
                  <span className="font-semibold text-ink">
                    {form.service_day}
                  </span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-slate-500">Address zip</span>
                  <span className="font-semibold text-ink">
                    {form.zip || ","}
                  </span>
                </li>
              </ul>
              <div className="rounded-xl bg-brand-50 border border-brand-100 p-4 mb-5" data-testid="signup-summary-charge">
                <div className="text-[11px] uppercase tracking-[0.2em] text-brand-700 font-bold mb-1">
                  {form.plan_code === "onetime" ? "One-time total" : "Today's charge"}
                </div>
                {promoState.status === "valid" && promoState.discount_percent > 0 ? (
                  <>
                    <div className="flex items-baseline gap-3">
                      <div className="font-heading text-4xl font-extrabold text-ink" data-testid="signup-summary-final">
                        ${(priceCents * (100 - promoState.discount_percent) / 10000).toFixed(2)}
                        <span className="text-sm text-slate-500 font-sans font-normal ml-1">
                          {form.plan_code === "onetime" ? "" : "/ mo"}
                        </span>
                      </div>
                      <span className="text-sm text-slate-400 line-through" data-testid="signup-summary-original">
                        ${priceDollars}
                      </span>
                    </div>
                    {monthlyExtraBins > 0 && (
                      <p className="mt-1 text-xs text-slate-500" data-testid="signup-summary-breakdown">
                        $25 base + $8 × {monthlyExtraBins} extra bin
                        {monthlyExtraBins === 1 ? "" : "s"}
                      </p>
                    )}
                    <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-accent-700" data-testid="signup-summary-discount-line">
                      <Sparkles size={12} />
                      {form.promo_code} saves you ${(priceCents * promoState.discount_percent / 10000).toFixed(2)}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="font-heading text-4xl font-extrabold text-ink" data-testid="signup-summary-final">
                      ${priceDollars}
                      <span className="text-sm text-slate-500 font-sans font-normal ml-1">
                        {form.plan_code === "onetime" ? "" : "/ mo"}
                      </span>
                    </div>
                    {monthlyExtraBins > 0 && (
                      <p className="mt-1 text-xs text-slate-500" data-testid="signup-summary-breakdown">
                        $25 base + $8 × {monthlyExtraBins} extra bin
                        {monthlyExtraBins === 1 ? "" : "s"}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <style>{`
        .signup-input {
          width: 100%;
          height: 3rem;
          border-radius: 0.75rem;
          border: 2px solid #E2E8F0;
          background: #fff;
          padding: 0.5rem 1rem;
          font-size: 1rem;
          color: #0F172A;
          transition: border-color .2s, box-shadow .2s;
        }
        .signup-input:focus {
          outline: none;
          border-color: #0EA5E9;
          box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.15);
        }
      `}</style>
    </div>
  );
}

function Field({ label, id, children, error, testId }) {
  return (
    <div data-testid={testId}>
      <label
        htmlFor={id}
        className="block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-2"
      >
        {label}
      </label>
      {children}
      {error && <p className="mt-1.5 text-xs text-red-500">{error}</p>}
    </div>
  );
}
